import React, {
  Suspense,
  useState,
  useEffect,
  useContext,
} from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import AppSnackbar from "./components/common/AppSnackbar";
// --- Contexts & Providers ---
import { AuthProvider, AuthContext } from "./context/AuthContext";
import AppearanceProvider, { useColorMode } from "./theme/AppearanceProvider";

// --- Components & Layouts ---
import Navbar from "./components/layout/Navbar";
import Sidebar from "./components/layout/Sidebar";
import ErrorBoundary from "./pages/errors/ErrorBoundary";
import ErrorPage from "./pages/errors/ErrorPage";
import OfflinePage from "./pages/errors/OfflinePage";
import RequireAuth from "./routes/RequireAuth";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import BottomTab from "./components/layout/BottomTab";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import SettingsPage from "./pages/dashboard/settings/SettingsPage";
import LicenseRequestsPage from "./pages/dashboard/license/LicenseRequestsPage";
import Licenses from "./pages/dashboard/license/licenses.jsx";
import Companies from "./pages/dashboard/users/Companies.jsx";
import Users from "./pages/dashboard/users/Users.jsx";
import Wallet from "./pages/dashboard/wallet/Wallet.jsx";
import AuditLogs from "./pages/dashboard/auditLogs/AuditLogs.jsx";
import BillingPayments from "./pages/dashboard/billing&Payments/BillingPayments.jsx";
import Tickets from "./pages/dashboard/tickets/Tickets.jsx";
import "./styles/admin-redesign.css";
import "./styles/global-buttons.css";
import "./styles/global-list-items.css";

// --- Lazy Load Pages ---
const Auth = React.lazy(() => import("./pages/auth/Auth"));
const Dashboard = React.lazy(() => import("./pages/dashboard/MainDashboard"));
const Profile = React.lazy(() => import("./pages/dashboard/profile/Profile"));
const OnboardingPage = React.lazy(() =>
  import("./pages/OnboardingPage")
);

// ==========================================
// 🏆  Color Mode Context
export { ColorModeContext } from "./theme/AppearanceProvider";

const LoadingScreen = () => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", bgcolor: "var(--background)" }}>
    <CircularProgress sx={{ color: "var(--primary)" }} />
    <Typography sx={{ mt: 2, color: "var(--text-secondary)" }}>
      Loading...
    </Typography>
  </Box>
);

const PublicLayout = ({ mode, setTheme }) => (
  <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
    {/* <Navbar mode={mode} setTheme={setTheme} isPublic={true} /> */}
    <Box component="main" sx={{ flex: 1 }}>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </Box>
  </Box>
);

// ==========================================
// 🏆  User Dashboard Layout
// ==========================================
const DashboardLayout = ({ user, mode, setTheme }) => {
  const location = useLocation();
  const [isLocked, setIsLocked] = useState(() => {
    try { return sessionStorage.getItem("admin-center.sidebar.expanded") === "true"; }
    catch { return false; }
  });
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const isExpanded = isLocked || (isMobile && offcanvasOpen);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setOffcanvasOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return undefined;
    let start = null;
    const onTouchStart = (event) => {
      const touch = event.touches[0];
      start = touch && (offcanvasOpen || touch.clientX <= 36)
        ? { x: touch.clientX, y: touch.clientY }
        : null;
    };
    const onTouchEnd = (event) => {
      if (!start) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) > 80 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        if (!offcanvasOpen && deltaX > 0) setOffcanvasOpen(true);
        if (offcanvasOpen && deltaX < 0) setOffcanvasOpen(false);
      }
      start = null;
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, offcanvasOpen]);

  useEffect(() => {
    setOffcanvasOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    try { sessionStorage.setItem("admin-center.sidebar.expanded", String(isLocked)); }
    catch { /* Keep the in-memory choice if storage is unavailable. */ }
  }, [isLocked]);

  return (
    <SidebarProvider
      open={isExpanded}
      onOpenChange={setIsLocked}
      openMobile={offcanvasOpen}
      onOpenMobileChange={setOffcanvasOpen}
      isMobile={isMobile}
    >
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "var(--background)" }}>
        <Sidebar
          user={user}
          isLocked={isLocked}
          setIsLocked={setIsLocked}
          offcanvasOpen={offcanvasOpen}
          setOffcanvasOpen={setOffcanvasOpen}
          isMobile={isMobile}
        />
        <SidebarInset
          className={`main-layout-wrapper ${isExpanded ? "pushed" : "compact"}`}
          style={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            transition: "margin 0.3s ease",
            paddingBottom: isMobile ? "70px" : "0px",
          }}
        >
          <Navbar
            user={user}
            mode={mode}
            setTheme={setTheme}
            isPublic={false}
            sidebarOpen={isExpanded}
          />
          <Box
            component="main"
            className="admin-page-content"
            sx={{
              px: { xs: 2, sm: 3, md: 6, lg: 9 },
              pt: { xs: 3, sm: 4, md: 6, lg: 9 },
              pb: { xs: 3, md: 6 },
              mt: { xs: "70px", md: "74px" },
              flex: 1,
              bgcolor: "var(--background)",
              color: "var(--page-foreground, var(--text-primary))",
            }}
          >
            <ErrorBoundary>
              <Outlet context={{ sidebarOpen: isExpanded }} />
            </ErrorBoundary>
          </Box>
          {isMobile && <BottomTab user={user} />}
        </SidebarInset>
      </Box>
    </SidebarProvider>
  );
};

const OnlineGuard = () => {
  const isOnline = useOnlineStatus();
  const location = useLocation();
  if (!isOnline) return <Navigate to="/offline" replace state={{ from: location }} />;
  return <Outlet />;
};

function UserAppearance() {
  const { user } = useContext(AuthContext);
  const userKey = user?.userId || user?.AC_USER_ID || "guest";
  return <AppearanceProvider key={userKey} userKey={String(userKey)}><AppRouteContent /></AppearanceProvider>;
}

function AppRouteContent() {
  const colorMode = useColorMode();
  const { mode, setTheme } = colorMode;
  return (
            <div className="admin-app">
            <AppSnackbar>
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  <Route element={<OnlineGuard />}>
                    <Route
                      element={
                        <PublicLayout
                          mode={mode}
                          setTheme={setTheme}
                        />
                      }
                    >
                      <Route path="/" element={<OnboardingPage />} />
                      <Route
                        path="/auth"
                        element={
                          <AuthContext.Consumer>
                            {({ handleAuthSuccess }) => (
                              <Auth onLoginSuccess={handleAuthSuccess} />
                            )}
                          </AuthContext.Consumer>
                        }
                      />
                    </Route>

                    <Route path="/dashboard" element={<RequireAuth />}>
                      <Route
                        element={
                          <AuthContext.Consumer>
                            {({ user, loading }) =>
                              loading ? (
                                <LoadingScreen />
                              ) : (
                                <DashboardLayout
                                  user={user}
                                  mode={mode}
                                  setTheme={setTheme}
                                />
                              )
                            }
                          </AuthContext.Consumer>
                        }
                      >
                        <Route index element={<Dashboard />} />
                        <Route
                          path="documents"
                          element={<Navigate to="FP" replace />}
                        />
                        <Route path="profile" element={<Profile />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="license-requests" element={<LicenseRequestsPage />} />
                        <Route path="licenses" element={<Licenses />} />
                        <Route path="companies" element={<Companies />} />
                        <Route path="users" element={<Users />} />
                        <Route path="tickets" element={<Tickets />} />
                        <Route path="wallet" element={<Wallet />} />
                        <Route path="billing" element={<BillingPayments />} />
                        <Route path="audit-logs" element={<AuditLogs />} />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="/offline" element={<OfflinePage />} />
                  <Route path="*" element={<ErrorPage />} />
                </Routes>

              </Suspense>
            </AppSnackbar>
            </div>
  );
}

const App = () => (
  <Router>
    <AuthProvider>
      <UserAppearance />
    </AuthProvider>
  </Router>
);
export default App;
